import OpeningHoursTable from './OpeningHoursTable';
import { OpeningHoursStatusStyled } from './OpeningHoursTable/OpeningHoursTable.style';
import {
    DescriptionStyled,
    ImagesWrapperStyled,
    InfoItemOpeningHoursStyled,
    InfoItemStyled,
    MapStyled,
    StoreBenefitsWrapperStyled,
    StoreDetailContentStyled,
    StoreDetailStyled,
    TelephoneStyled,
} from './StoreDetail.style';
import Heading from 'components/Basic/Heading/Heading';
import Image from 'components/Basic/Image/Image';
import { GoogleMap } from 'components/Basic/Map/GoogleMap/GoogleMap';
import { PigeonMap } from 'components/Basic/Map/PigeonMap/PigeonMap';
import { SeznamMap } from 'components/Basic/Map/SeznamMap/SeznamMap';
import SelectStoreButton from 'components/Blocks/Store/SelectStoreButton/SelectStoreButton';
import StoreBenefits from 'components/Blocks/Store/StoreBenefits';
import UserText from 'components/Helpers/UserText/UserText';
import Webline from 'components/Layout/Webline/Webline';
import { MapProvider } from 'context/Map/MapProvider';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import dynamic from 'next/dynamic';
import { useMemo, useState } from 'react';
import { MapMarkerType, MapProviderTypeEnum } from 'types/map';
import { StoreDetailType } from 'types/store';

const Gallery = dynamic(() => import('components/Basic/Gallery/Gallery').then((component) => component.Gallery));

type StoreDetailPageProps = {
    store: StoreDetailType;
    fetching: boolean;
};

const TEST_IDENTIFIER = 'pages-storedetail';

export const StoreDetail: FC<StoreDetailPageProps> = ({ store, fetching }) => {
    const t = useTypedTranslationFunction();
    const [activeGalleryIndex, setActiveGalleryIndex] = useState<number | undefined>();

    const markers = useMemo(
        () => [
            {
                identifier: store.uuid,
                lat: store.locationLatitude,
                lng: store.locationLongitude,
                storeName: store.storeName,
                storeStreet: store.street,
                storeCity: store.city,
                storeSlug: store.slug,
                isPickupPoint: store.isPickupPoint,
            } as MapMarkerType,
        ],
        [store],
    );

    const mapSetting = useMemo(
        () => ({
            latitude: store.locationLatitude!,
            longitude: store.locationLongitude!,
            zoom: 15,
        }),
        [store],
    );

    const hasSomeVisibleBenefits: boolean =
        (store.acceptGiftCoupon && !!store.acceptGiftCouponIcon) ||
        (store.hasLoyaltyProgram && !!store.hasLoyaltyProgramIcon) ||
        (store.isPickupPoint && !!store.isPickupPointIcon);

    return (
        <Webline data-testid={TEST_IDENTIFIER}>
            <StoreDetailStyled>
                <StoreDetailContentStyled>
                    <Heading type="h1">{store.storeName}</Heading>
                    <div>
                        {store.isPickupPoint && <SelectStoreButton storeUuid={store.uuid} />}
                        {store.description !== null && (
                            <DescriptionStyled>
                                <UserText htmlContent={store.description} />
                            </DescriptionStyled>
                        )}
                        {store.openingHours !== null &&
                            store.openingHours.days.some(({ alwaysClosed }) => !alwaysClosed) && (
                                <InfoItemOpeningHoursStyled>
                                    <OpeningHoursStatusStyled isOpen={store.openingHours.isCurrentlyOpen}>
                                        {store.openingHours.isCurrentlyOpen ? t('Open') : t('Closed')}
                                    </OpeningHoursStatusStyled>
                                    <Heading type="h6">{t('Opening hours')}</Heading>
                                    <OpeningHoursTable {...store.openingHours} />
                                </InfoItemOpeningHoursStyled>
                            )}
                        <Heading type="h6">{t('Address')}</Heading>
                        <InfoItemStyled>
                            {`${store.street} ${store.city} ${store.postcode}`}
                            <br />
                            {t('Email address')}:{' '}
                            {store.email !== null && <a href={'mailto:' + store.email}>{store.email}</a>}
                        </InfoItemStyled>

                        {store.telephone !== null && (
                            <TelephoneStyled>
                                {t('Phone')}: <a href={'tel:' + store.telephone}>{store.telephone}</a>
                            </TelephoneStyled>
                        )}

                        {store.locationLatitude !== null && store.locationLongitude !== null && (
                            <InfoItemStyled>
                                {`${t('GPS')}: ${store.locationLatitude
                                    .toString()
                                    .replace('.', ',')}N ${store.locationLongitude.toString().replace('.', ',')}E`}
                            </InfoItemStyled>
                        )}
                        {store.isOperatedByTescoma && (
                            <div>{t('Sales center operated by the company TESCOMA s.r.o. (IČ: 46981691)')}</div>
                        )}
                        {hasSomeVisibleBenefits && (
                            <StoreBenefitsWrapperStyled>
                                <Heading type="h6">{t('In-store services')}:</Heading>
                                <StoreBenefits
                                    acceptGiftCoupon={store.acceptGiftCoupon}
                                    acceptGiftCouponIcon={store.acceptGiftCouponIcon}
                                    hasLoyaltyProgram={store.hasLoyaltyProgram}
                                    hasLoyaltyProgramIcon={store.hasLoyaltyProgramIcon}
                                    isPickupPoint={store.isPickupPoint}
                                    isPickupPointIcon={store.isPickupPointIcon}
                                    isInline={false}
                                />
                            </StoreBenefitsWrapperStyled>
                        )}
                    </div>
                    {store.locationLatitude !== null && store.locationLongitude !== null && (
                        <MapStyled>
                            <MapProvider initSetting={mapSetting}>
                                {(provider: string | undefined) => {
                                    if (provider === MapProviderTypeEnum.OSM) {
                                        return <PigeonMap markers={markers} shouldShowSpinner={fetching} />;
                                    }
                                    if (provider === MapProviderTypeEnum.SEZNAM) {
                                        return <SeznamMap markers={markers} shouldShowSpinner={fetching} />;
                                    }
                                    if (provider === MapProviderTypeEnum.GOOGLE) {
                                        return <GoogleMap markers={markers} shouldShowSpinner={fetching} />;
                                    }

                                    return null;
                                }}
                            </MapProvider>
                        </MapStyled>
                    )}
                </StoreDetailContentStyled>
                {store.images.length > 0 && (
                    <ImagesWrapperStyled>
                        <div className="flex flex-wrap items-center justify-center lg:justify-start">
                            {store.images.map((image, index) => (
                                <button
                                    className="m-2.5 xl:m-5"
                                    key={image}
                                    onClick={() => setActiveGalleryIndex(index)}
                                >
                                    <Image
                                        alt={store.storeName}
                                        src={image}
                                        height="167"
                                        width="250"
                                        className="h-[167px] w-[250px]"
                                    />
                                </button>
                            ))}
                        </div>
                        {activeGalleryIndex !== undefined && (
                            <Gallery
                                startIndex={activeGalleryIndex}
                                galleryItems={store.images.map((image) => ({ src: image }))}
                                onCloseCallback={() => setActiveGalleryIndex(undefined)}
                            />
                        )}
                    </ImagesWrapperStyled>
                )}
            </StoreDetailStyled>
        </Webline>
    );
};

export default StoreDetail;
